import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuthSession } from './AuthSessionContext';
import { CircularProgress, Container, Snackbar, Alert } from '@mui/material';
import axiosInstance from './axiosInstance';
import SupplierGridView from './SupplierGridView';
import SupplierPage from './SupplierPage';
import { formatDate, revertDateFormat } from './dateUtils';
import './ReviewSupplierPage.css'; // Import the new CSS file


const ReviewSupplierPage = ({ showImportButton = true, createButtonLabel = null, containerClassName = '' }) => {
  const { roles: userRoles } = useAuthSession();
  const isAdmin = userRoles.includes('admin');
  const isReaderOnly =
    userRoles.includes('reader') &&
    !userRoles.some((role) => role === 'admin' || role === 'reviewer');

  const normaliseErrorMessage = (payload) => {
  if (!payload) {
    return 'An unexpected error occurred.';
  }

  if (typeof payload === 'string') {
    return payload;
  }

  if (typeof payload === 'object') {
    if (payload.message) {
      return payload.message;
    }
    if (payload.detail) {
      return payload.detail;
    }
    if (payload.error) {
      return payload.error;
    }
    if (Array.isArray(payload)) {
      return payload.filter(Boolean).join(', ');
    }
    try {
      return JSON.stringify(payload);
    } catch (_error) {
      return 'An unexpected error occurred.';
    }
  }

  return String(payload);
};

const defaultSelectedDocStatuses = [1, 8, 9];
  const pageLimit = 10; // Set the limit per page for easy access
  const [loading, setLoading] = useState(true);
  const [dataList, setDataList] = useState([]);
  const [filteredDataList, setFilteredDataList] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [detailedData, setDetailedData] = useState(null);
  const [editableData, setEditableData] = useState(null);
  const [tempEditableData, setTempEditableData] = useState(null);
  const [locations, setLocations] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [nominalCodes, setNominalCodes] = useState([]);
  const [docStatuses, setDocStatuses] = useState([]);
  const [selectedSupplier, setSelectedSupplier] = useState('');
  const [selectedLocation, setSelectedLocation] = useState('');
  const [sortCriteria, setSortCriteria] = useState('');
  const [sortOrder, setSortOrder] = useState('asc');
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isEdited, setIsEdited] = useState(false);
  const [zoomEnabled, setZoomEnabled] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalDocuments, setTotalDocuments] = useState(0); // Add totalDocuments state
  const [allowedDocStatuses, setAllowedDocStatuses] = useState(['Approved', 'Export In Progress', 'Export Failed', 'Duplicate Export', 'Exported']);
  const [selectedDocStatuses, setSelectedDocStatuses] = useState(defaultSelectedDocStatuses); // Set to 1 by default
  const [notification, setNotification] = useState({ message: '', type: '', open: false }); // Add state for notifications

  const [statusMessage, setStatusMessage] = useState({ type: '', message: '' });
  const [showJobBox, setShowJobBox] = useState(false);
  const [dumpInProgress, setDumpInProgress] = useState(false);
  const [processedDocs, setProcessedDocs] = useState(0);
  const [totalDocs, setTotalDocs] = useState(0);
  const [exportDocsCount, setExportDocsCount] = useState(0);
  const [sortedData, setSortedData] = useState([]);
  
  

  const transformWrapperRef = useRef(null);

  // Define handlePreviousImage function
  const handlePreviousImage = useCallback(() => {
    setCurrentImageIndex((prevIndex) => (prevIndex > 0 ? prevIndex - 1 : prevIndex));
    zoomOut();
    setZoomEnabled(false);
  }, []);

  // Define handleNextImage function
  const handleNextImage = useCallback(() => {
    setCurrentImageIndex((prevIndex) => (prevIndex < detailedData.images.length - 1 ? prevIndex + 1 : prevIndex));
    zoomOut();
    setZoomEnabled(false);
  }, [detailedData]);


  // Use useCallback to define fetchData
  const fetchData = useCallback(async (page = 1) => {
    setLoading(true);
    try {
        const queryObject = {
          page,
          limit: pageLimit,
        };

        if (sortCriteria) {
          queryObject.sort = { [sortCriteria]: sortOrder };
        }

        const filter = {};
        if (selectedSupplier) {
            filter.supplier_id = selectedSupplier;
        }
        if (debouncedSearchTerm) {
            const regex = { $regex: debouncedSearchTerm, $options: 'i' };
            filter.$or = [
              { supplier_id: regex },
              { name: regex },
              { address: regex },
              { telephone: regex },
              { email: regex },
              { website: regex },
            ];
        }

        // Add filter to queryObject if there are filter conditions
        if (Object.keys(filter).length > 0) {
            queryObject.filter = filter;
        }

        const queryBase64 = await fetchBase64(queryObject);
        //console.log('Query base64:', queryBase64);

        const [
          uploadsResponse,
          suppliersResponse,
        ] = await Promise.all([
          axiosInstance.get(`/api/v1/supplier/mapping?query=${queryBase64}`),
          axiosInstance.get(`/api/v1/supplier`),
        ]);

        //console.log('Uploads response:', uploadsResponse.data);

        const formattedDocuments = uploadsResponse.data.data.documents.map(doc => ({
            ...doc,
            created_on: formatDate(doc.created_on), // Format "created_on" date
        }));
        //console.log('image name:', formattedDocuments[0].image_name)

        const totalDocs = uploadsResponse.data.data.total_documents || 0;
        const totalPages = Math.ceil(totalDocs / pageLimit);

        // Update state only once at the end of this function
        setDataList(formattedDocuments);
        setFilteredDataList(formattedDocuments); // Set filteredDataList based on fetched data
        setSuppliers(suppliersResponse.data.data || []);
        setTotalDocuments(totalDocs);
        setTotalPages(totalPages);

        setLoading(false);
        return formattedDocuments;

    } catch (error) {
        setLoading(false);
        console.error('Error fetching data:', error);
    }
  }, [sortCriteria, sortOrder, selectedSupplier, selectedLocation, selectedDocStatuses, allowedDocStatuses, currentPage, debouncedSearchTerm]);

  const fetchBase64 = async (queryObject) => {
    try {
      const response = await axiosInstance.post(
        `/api/v1/text/base64`,
        queryObject
      );
      return response.data.data; // Ensure we're returning only the base64 string from the response
    } catch (error) {
      console.error('Error fetching base64:', error);
      throw error;
    }
  };

  useEffect(() => {
    // console.log("==================================Current Page change==================", currentPage);
    fetchData(currentPage); // Fetch data on component mount with the initial page
  }, [currentPage, fetchData]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm.trim());
    }, 350);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchTerm]);

  const handleCardClick = async (mapping) => {
    setLoading(true);
    try {
      let formattedData = {};
      if (mapping?.id) {
        const response = await axiosInstance.get(`/api/v1/supplier/mapping/${mapping.id}`);
        const data = response.data.data;
        console.log("Data", data);
        formattedData = {
          ...data,
          created_on: formatDate(data.created_on), // Format the date to the desired format
        };
      }
      setDetailedData(formattedData);
      setEditableData(formattedData);
      setTempEditableData(formattedData);
      setCurrentImageIndex(0);
      //console.log('Detailed data:', formattedData);
      if (zoomEnabled) {
        zoomOut();
        setZoomEnabled(false);
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.error('Error fetching detailed data:', error);
    }
  };

  const getImageName = () => {
    const matchingDoc = filteredDataList.find(doc => doc.id === detailedData?.id);
    const fullImageName = matchingDoc?.images?.[0] || 'No Image';
    const imageNameParts = fullImageName.split('_');
    return imageNameParts.length > 1 ? imageNameParts[1] : fullImageName;
  };

  const handleFieldChange = (field, value) => {
    if (isReaderOnly) return;
    setTempEditableData((prevData) => ({
      ...prevData,
      [field]: value,
    }));

    setIsEdited(true);
  };

  const handleFieldBlur = (field, value) => {
    if (isReaderOnly) return;
    const formattedValue = parseFloat(value).toFixed(2);
    setTempEditableData((prevData) => ({
      ...prevData,
      [field]: formattedValue,
    }));
  };

  const handleBackClick = () => {
    // console.log("==================================Handle back click==================", currentPage);
    setDetailedData(null);
    setEditableData(null);
    setTempEditableData(null);
    setIsEdited(false);
    fetchData(currentPage);
    // setCurrentPage(1); // Reset to page 1 when returning to GridView
  };

  const handleSupplierChange = (event) => {
    const supplierValue = event.target.value;
    setSelectedSupplier(supplierValue);

    if (showImportButton) {
      filterData(supplierValue, selectedLocation);
    } else {
      setCurrentPage(1);
    }
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleClearFilters = () => {
    setSelectedSupplier('');
    setSelectedLocation('');
    setSelectedDocStatuses(defaultSelectedDocStatuses); // Reset to default
    setSortCriteria('');
    setSortOrder('asc');

    if (showImportButton) {
      setFilteredDataList(dataList);
    } else {
      setCurrentPage(1);
    }
  };

  const filterData = (supplier = selectedSupplier, locationId = selectedLocation, selectedDocStatuses) => {
    let filteredData = dataList; // Use dataList as the base dataset
    if (supplier) {
      filteredData = filteredData.filter((d) => d.supplier_id === supplier);
    }
    
    if (locationId) {
      filteredData = filteredData.filter((d) => d.location_id === locationId);
    }

    if (selectedDocStatuses) {
      if (selectedDocStatuses && selectedDocStatuses.length == 0) {
        selectedDocStatuses = docStatuses.map(d => d.doc_status_id);
      }
      filteredData = filteredData.filter((d) => selectedDocStatuses.includes(d.doc_status_id));
    }
    
    setFilteredDataList(filteredData);
  };

  const createSupplierMapping = async (updatedData) => {
    // Ensure only the necessary fields are sent
    const formattedData = {
      supplier_id: updatedData.supplier_id,
      name: updatedData.name,
      address: updatedData.address,
      telephone: updatedData.telephone,
      email: updatedData.email,
      website: updatedData.website
    };
  
    try {
      const response = await axiosInstance.post(`/api/v1/supplier/mapping`, formattedData);
      
      //console.log('Update successful:', response.data);
      return response.data; // Return response data to check success in calling functions
    } catch (error) {
      // console.error('Error creating supplier mapping:', error);
      throw error.response.data; // Throw error response data
    }
  };


  const updateSupplierMapping = async (updatedData) => {
    // Ensure only the necessary fields are sent
    const formattedData = {
      id: updatedData.id,
      supplier_id: updatedData.supplier_id,
      name: updatedData.name,
      address: updatedData.address,
      telephone: updatedData.telephone,
      email: updatedData.email,
      website: updatedData.website
    };
  
    try {
      const response = await axiosInstance.put(`/api/v1/supplier/mapping`, formattedData);
      
      //console.log('Update successful:', response.data);
      return response.data; // Return response data to check success in calling functions
    } catch (error) {
      // console.error('Error updating supplier mapping:', error);
      throw error.response.data; // Throw error response data
    }
  };

  const deleteSupplierMapping = async (supplier_mapping_id) => {  
    try {
      const response = await axiosInstance.delete(`/api/v1/supplier/mapping/${supplier_mapping_id}`);
      
      setNotification({ message: "Mapping deleted sucessfully.", type: 'success', open: true });
      fetchData(currentPage);
      return response.data; // Return response data to check success in calling functions
    } catch (error) {
      let errorMessage = 'Deletion failed. Please try again.';
      const errorData = error?.response?.data;
      if (errorData?.errors && Array.isArray(errorData?.errors)) {
        errorMessage = errorData.errors.map(err => err?.msg).join(', ');
      } else if (errorData?.error) {
        errorMessage = errorData.error;
      }
      setNotification({ message: errorMessage, type: 'error', open: true });
    }
  };

  // Modified advanceToNextDocument function
  const advanceToNextDocument = async () => {
    try {
      // Fetch data for the current page
      const data = await fetchData(currentPage);
      //console.log('Current page data:', data);

      if (data.length > 0) {
        // If there's data on the current page, display the first document
        handleCardClick(data[0]);
      } else if (currentPage > 1) {
        // If the current page is empty and is not the first page, go to the first page
        const firstPageData = await fetchData(1);
        //console.log('First page data:', firstPageData);

        if (firstPageData.length > 0) {
          // If there's data on page 1, display the first document
          handleCardClick(firstPageData[0]);
        } else {
          // If both current page and page 1 are empty, return to GridView
          handleBackClick();
        }
      } else {
        // If currentPage is 1 and there's no data
        handleBackClick();
      }
    } catch (error) {
      console.error('Error advancing to the next document:', error);
    }
  };

  const handleSaveChanges = async () => {
    if (isReaderOnly) return;
    const formattedData = {
      ...tempEditableData
    };
  
    try {
      if (formattedData?.id) {
        await updateSupplierMapping(formattedData);
        setNotification({ message: 'Mapping updated successfully', type: 'success', open: true });
        handleBackClick();
     
      } else {
        await createSupplierMapping(formattedData);
        setNotification({ message: 'Mapping created successfully', type: 'success', open: true });
        handleBackClick();
     
      }

      setEditableData(formattedData);
      setIsEdited(false);

    } catch (errorData) {
      let errorMessage = 'Submission failed. Please try again.';
      if (errorData.errors && Array.isArray(errorData.errors)) {
        errorMessage = errorData.errors.map(err => err.msg).join(', ');
      } else if (errorData.error) {
        errorMessage = errorData.error;
      }
      setNotification({ message: errorMessage, type: 'error', open: true });
      setDetailedData(tempEditableData);
      setEditableData(tempEditableData);
      setTempEditableData(tempEditableData);
    }
  };

  const handleZoomToggle = () => {
    setZoomEnabled((prevZoomEnabled) => {
      if (prevZoomEnabled) {
        zoomOut();
      }
      return !prevZoomEnabled;
    });
  };

  const zoomOut = () => {
    if (transformWrapperRef.current) {
      transformWrapperRef.current.resetTransform();
    }
  };

  const getLocationOptionLabel = (option) => {
    if (typeof option === 'string') {
      return option;
    }
    return `${option.name}`;
  };

  const getSupplierOptionLabel = (option) => {
    if (typeof option === 'string') {
      return option;
    }
    return `${option.supplier_id} - ${option.name}`;
  };

  const getNominalCodeOptionLabel = (option) => {
    if (!option) {
      return '';
    }
    if (typeof option === 'string') {
      return option;
    }
    return `${option.nominal_code_id} - ${option.name}`;
  };

  const currentPageRef = useRef(currentPage);
  const pageLimitRef = useRef(pageLimit);
  const sortCriteriaRef = useRef(sortCriteria);
  const sortOrderRef = useRef(sortOrder);
  const selectedSupplierRef = useRef(selectedSupplier);
  const selectedLocationRef = useRef(selectedLocation);
  const selectedDocStatusesRef = useRef(selectedDocStatuses);
  const filteredDataListRef = useRef(filteredDataList);

  useEffect(() => {
    currentPageRef.current = currentPage;
    pageLimitRef.current = pageLimit;
    sortCriteriaRef.current = sortCriteria;
    sortOrderRef.current = sortOrder;
    selectedSupplierRef.current = selectedSupplier;
    selectedLocationRef.current = selectedLocation;
    selectedDocStatusesRef.current = selectedDocStatuses;
    filteredDataListRef.current = filteredDataList;
  }, [currentPage, pageLimit, sortCriteria, sortOrder, selectedSupplier, selectedLocation, selectedDocStatuses, filteredDataList]);


  const dumpSuppliers = async () => {

    try {
      setDumpInProgress(true);
      const response = await axiosInstance.post('/api/v1/supplier/import');
      
      if (response.status === 200) {
        console.log(response.data.data);
        setSuppliers(response.data.data);
        setStatusMessage({type: 'success', message: "Suppliers imported successfully."});
        setDumpInProgress(false);

      } else {
        setDumpInProgress(false);
      
      }
      
    } catch (error) {
      setDumpInProgress(false);
      setStatusMessage({type: 'error', message: normaliseErrorMessage(error?.response?.data) || error?.message});
      console.error('Error while importing suppliers.', error);

    }

  };

  const refetchData = async () => {
    setLoading(true);
    await fetchData(currentPage); // Fetch the data again to ensure it's up-to-date
    setLoading(false);
  };

  if (showImportButton && loading) {
    return <CircularProgress />;
  }

  return (
    <Container className={`fullScreenContainer ${containerClassName}`.trim()} maxWidth={false}>
      {notification.open && (
        <Snackbar open={notification.open} autoHideDuration={6000} onClose={() => setNotification({ ...notification, open: false })}>
          <Alert onClose={() => setNotification({ ...notification, open: false })} severity={notification.type}>
            {notification.message}
          </Alert>
        </Snackbar>
      )}
      {detailedData ? (
        <SupplierPage
          detailedData={detailedData}
          editableData={editableData}
          tempEditableData={tempEditableData}
          setTempEditableData={setTempEditableData}
          sites={locations}
          suppliers={suppliers}
          nominalCodes={nominalCodes}
          docStatuses={docStatuses}
          currentImageIndex={currentImageIndex}
          isEdited={isEdited}
          zoomEnabled={zoomEnabled}
          transformWrapperRef={transformWrapperRef}
          handleBackClick={handleBackClick}
          handleFieldChange={handleFieldChange}
          handleFieldBlur={handleFieldBlur}
          handlePreviousImage={handlePreviousImage}
          handleNextImage={handleNextImage}
          handleZoomToggle={handleZoomToggle}
          handleSaveChanges={handleSaveChanges}
          otherDocuments={filteredDataList.filter(doc => doc.id !== detailedData?.id) || []}
          handleCardClick={handleCardClick}
          getLocationOptionLabel={getLocationOptionLabel}  
          getSupplierOptionLabel={getSupplierOptionLabel}
          getNominalCodeOptionLabel={getNominalCodeOptionLabel}
          refetchData={refetchData} // Pass refetchData to ReviewPage
          imageName={getImageName()} // pass image name here
          advanceToNextDocument={advanceToNextDocument}
          isMappingMode={!showImportButton}
          isReadOnly={isReaderOnly}
        />
      ) : (
        <SupplierGridView
          locations={locations}
          suppliers={suppliers}
          filteredDataList={Array.isArray(filteredDataList) ? filteredDataList : []}
          handleCardClick={handleCardClick}
          selectedSupplier={selectedSupplier}
          selectedLocation={selectedLocation}
          handleSupplierChange={handleSupplierChange}
          handleClearFilters={handleClearFilters}
          sortCriteria={sortCriteria}
          setSortCriteria={setSortCriteria}
          sortOrder={sortOrder}
          setSortOrder={setSortOrder}
          currentPage={currentPage}
          totalPages={totalPages} 
          handlePageChange={handlePageChange}
          totalDocuments={totalDocuments} 
          pageLimit={pageLimit} 
          selectedDocStatuses={selectedDocStatuses}
          deleteSupplierMapping={deleteSupplierMapping}
          docStatuses={docStatuses}
          dumpSuppliers={dumpSuppliers}
          dumpInProgress={dumpInProgress}
          processedDocs={processedDocs}
          setProcessedDocs={setProcessedDocs}
          totalDocs={totalDocs}
          setTotalDocs={setTotalDocs}
          showJobBox={showJobBox}
          setShowJobBox={setShowJobBox}
          exportDocsCount={exportDocsCount}
          setExportDocsCount={setExportDocsCount}
          sortedData={sortedData}
          setSortedData={setSortedData}
          statusMessage={statusMessage}
          setStatusMessage={setStatusMessage}
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          showImportButton={showImportButton}
          createButtonLabel={createButtonLabel}
          isDataLoading={!showImportButton ? loading : false}
          isAdmin={isAdmin}
          isReadOnly={isReaderOnly}
        />
      )}
    </Container>
  );
};


export default ReviewSupplierPage;
